/**
 * Uploading documents, as a page-level action beside the heading. The button
 * opens the upload dialog: several PDFs at once, each filed as a new policy
 * or as the next version of one already here, with jurisdictions for the new
 * ones.
 *
 * @module components/regulation/PolicyUploadAction
 */
import { UploadIcon } from "lucide-react";
import { useState } from "react";

import { useOrgProfile } from "../../state/orgProfile";
import { Button } from "../ui/button";
import { PolicyUploadDialog } from "./PolicyUploadDialog";
import { useRegulationOrg } from "./useRegulationOrg";

export function PolicyUploadAction({
  documentKind = "policy",
}: {
  /** Which shelf the page is; the button says what it will add. */
  readonly documentKind?: "policy" | "procedure";
} = {}) {
  const { org } = useRegulationOrg();
  const { canManage } = useOrgProfile(org?.id ?? null);
  const [open, setOpen] = useState(false);

  if (!org || !canManage) {
    return null;
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="cursor-pointer gap-1.5"
        onClick={() => setOpen(true)}
      >
        <UploadIcon className="size-3.5" />
        {documentKind === "procedure" ? "Upload procedures" : "Upload policies"}
      </Button>
      <PolicyUploadDialog
        organizationId={org.id}
        open={open}
        onOpenChange={setOpen}
        documentKind={documentKind}
      />
    </>
  );
}
